import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { toolRegistry, toolsByCategory, ToolCategory, ToolDef } from "@/lib/tool-registry";
import { Menu, Search, Wrench, ChevronRight, X } from "lucide-react";
import React, { useState, useMemo, useRef, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { HelmetProvider, Helmet } from "react-helmet-async";

// Import Custom Utility Pages
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { TermsOfService } from "@/components/TermsOfService";
import { AboutUs } from "@/components/AboutUs";
import { Contact } from "@/components/Contact";
import { Documentation } from "@/components/Documentation";
import { ApiInfo } from "@/components/ApiInfo";

// SEO Component
const SEO = ({ title, description }: { title: string; description: string }) => (
  <Helmet>
    <title>{title} – 247 DevTools Hub</title>
    <meta name="description" content={description} />
  </Helmet>
);

// Import Tools
import { WordCounter } from "@/tools/text/WordCounter";
import { TextCaseConverter } from "@/tools/text/TextCaseConverter";
import { DuplicateLineRemover } from "@/tools/text/DuplicateLineRemover";
import { LoremIpsumGenerator } from "@/tools/text/LoremIpsumGenerator";
import { WhitespaceRemover } from "@/tools/text/WhitespaceRemover";
import { CharacterCounter, ReadingTimeCalculator, WordsToPagesConverter, LineBreakRemover } from "@/tools/text/TextTools1";
import { TextReverser, TextSorter, FindAndReplaceTool, RandomGenerator } from "@/tools/text/TextTools2";
import { TextDiffChecker } from "@/tools/text/TextDiffChecker";

import { JsonFormatter } from "@/tools/json/JsonFormatter";
import { JsonMinifier } from "@/tools/json/JsonMinifier";
import { YamlFormatter } from "@/tools/json/YamlFormatter";
import { JsonValidator, JsonToCsvConverter, JsonToXmlConverter, CsvToJsonConverter } from "@/tools/json/DataTools1";
import { XmlFormatter, SqlFormatterTool, CsvViewer } from "@/tools/json/DataTools2";
import { CsvEditor } from "@/tools/data/CsvEditor";
import { ExcelEditor } from "@/tools/data/ExcelEditor";
import { CsvGenerator } from "@/tools/data/CsvGenerator";

import { HashGenerator } from "@/tools/crypto/HashGenerator";
import { PasswordGenerator } from "@/tools/crypto/PasswordGenerator";
import { UuidGenerator } from "@/tools/crypto/UuidGenerator";
import { BcryptGenerator } from "@/tools/crypto/BcryptGenerator";
import { PasswordStrengthChecker, HashComparator, HmacGenerator, RandomStringGenerator } from "@/tools/crypto/CryptoTools1";
import { JwtGenerator } from "@/tools/crypto/JwtGenerator";
import { OAuthUrlGenerator } from "@/tools/crypto/OAuthUrlGenerator";
import { PkceGenerator } from "@/tools/crypto/PkceGenerator";
import { CsrfTokenGenerator } from "@/tools/crypto/CsrfTokenGenerator";
import { NanoIdGenerator } from "@/tools/crypto/NanoIdGenerator";

import { Base64Tool } from "@/tools/encoding/Base64";
import { UrlEncoderTool } from "@/tools/encoding/UrlEncoder";
import { HtmlEntityEncoder, JwtDecoder, UnicodeAsciiConverter, PunycodeConverter } from "@/tools/encoding/EncodingTools1";
import { HexTextConverter, BinaryTextConverter, MorseCodeTranslator } from "@/tools/encoding/EncodingTools2";

import { QrCodeGenerator } from "@/tools/dev/QrCodeGenerator";
import { CssFormatter } from "@/tools/dev/CssFormatter";
import { HtmlFormatter, JsFormatter, Minifiers, CodeDiffViewer } from "@/tools/dev/DevTools1";

import { RegexTester, RegexCheatSheet, RegexGenerator } from "@/tools/regex/RegexTools";

import { HexRgbConverter } from "@/tools/color/HexRgbConverter";
import { ColorPickerTool, HslConverter, ColorPaletteGenerator, ContrastChecker } from "@/tools/color/ColorTools";
import { GradientGenerator, ColorNameFinder } from "@/tools/color/ColorTools2";

import { UnixTimestampConverter } from "@/tools/datetime/UnixTimestampConverter";
import { AgeCalculator, DateDifference, CronParser } from "@/tools/datetime/DateTimeTools";
import { TimezoneConverter, CountdownTimerGenerator } from "@/tools/datetime/DateTimeTools2";

import { BarcodeGenerator, MetaTagGenerator, UserAgentParser } from "@/tools/dev/WebTools";
import { HtaccessGenerator, RobotsTxtGenerator, OpenGraphPreview, FaviconGenerator } from "@/tools/dev/WebTools2";

import { PercentageCalc, BaseConverter, UnitConverter } from "@/tools/number/NumberTools";
import { RandomNumberGenerator, GpaCalculator, BmiCalculator, LoanCalculator } from "@/tools/number/NumberTools2";

import { MarkdownPreviewer } from "@/tools/misc/MarkdownPreviewer";
import { PlaceholderImageGenerator } from "@/tools/misc/MiscTools";
import { FakeDataGenerator, ImageToBase64, SlugGenerator, EmojiPickerTool } from "@/tools/misc/MiscTools2";

import { CurlBuilder, CurlToFetch, CurlToAxios } from "@/tools/dev/CurlTools";
import { TomlJsonConverter } from "@/tools/json/TomlJsonConverter";
import { CidrCalculator, SubnetCalculator, IpRangeCalculator, Ipv6Converter, MacAddressGenerator } from "@/tools/network/NetworkTools";
import { AdSenseAuditor } from "@/tools/dev/AdSenseAuditor";

// Import SVG Tools
import { SvgOptimizer } from "@/tools/svg/SvgOptimizer";
import { SvgCompressor } from "@/tools/svg/SvgCompressor";
import { SvgViewer } from "@/tools/svg/SvgViewer";
import { SvgMinifier } from "@/tools/svg/SvgMinifier";
import { SvgPathEditor } from "@/tools/svg/SvgPathEditor";
import { SvgToReact } from "@/tools/svg/SvgToReact";
import { SvgToDataUri } from "@/tools/svg/SvgToDataUri";
import { EcosystemSection } from "@/components/EcosystemSection";
import { FileDropProvider } from "@/context/FileDropContext";
import { CategoryPage } from "@/components/CategoryPage";

// Fallback for unimplemented tools
const PlaceholderTool = ({ name }: { name: string }) => (
  <div className="py-12 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[300px]">
    <Wrench className="w-12 h-12 mb-4 text-slate-300" />
    <h3 className="text-lg font-medium text-slate-900 mb-1">{name}</h3>
    <p>This tool is currently under construction.</p>
  </div>
);

// Map components
const componentMap: Record<string, React.FC<any>> = {
  WordCounter,
  CharacterCounter,
  ReadingTimeCalculator,
  WordsToPagesConverter,
  TextCaseConverter,
  DuplicateLineRemover,
  LineBreakRemover,
  TextReverser,
  TextSorter,
  FindAndReplaceTool,
  TextDiffChecker,
  LoremIpsumGenerator,
  RandomGenerator,
  WhitespaceRemover,

  JsonFormatter,
  JsonMinifier,
  YamlFormatter,
  JsonValidator,
  JsonToCsvConverter,
  JsonToXmlConverter,
  CsvToJsonConverter,
  XmlFormatter,
  SqlFormatterTool,
  CsvViewer: CsvEditor,
  CsvEditor,
  ExcelEditor,
  CsvGenerator,

  HashGenerator,
  PasswordGenerator,
  UuidGenerator,
  BcryptGenerator,
  PasswordStrengthChecker,
  HashComparator,
  HmacGenerator,
  RandomStringGenerator,
  JwtGenerator,
  OAuthUrlGenerator,
  PkceGenerator,
  CsrfTokenGenerator,
  NanoIdGenerator,

  Base64Tool,
  UrlEncoderTool,
  HtmlEntityEncoder,
  JwtDecoder,
  UnicodeAsciiConverter,
  PunycodeConverter,
  HexTextConverter,
  BinaryTextConverter,
  MorseCodeTranslator,

  QrCodeGenerator,
  CssFormatter,
  HtmlFormatter,
  JsFormatter,
  Minifiers,
  CodeDiffViewer,

  RegexTester,
  RegexCheatSheet,
  RegexGenerator,

  HexRgbConverter,
  ColorPickerTool,
  HslConverter,
  ColorPaletteGenerator,
  GradientGenerator,
  ContrastChecker,
  ColorNameFinder,

  UnixTimestampConverter,
  TimezoneConverter,
  AgeCalculator,
  CountdownTimerGenerator,
  DateDifference,
  CronParser,

  BarcodeGenerator,
  MetaTagGenerator,
  FaviconGenerator,
  HtaccessGenerator,
  RobotsTxtGenerator,
  UserAgentParser,
  OpenGraphPreview,

  PercentageCalc,
  BaseConverter,
  UnitConverter,
  RandomNumberGenerator,
  GpaCalculator,
  BmiCalculator,
  LoanCalculator,

  MarkdownPreviewer,
  PlaceholderImageGenerator,
  FakeDataGenerator,
  ImageToBase64,
  SvgOptimizer,
  SvgCompressor,
  SvgViewer,
  SvgMinifier,
  SvgPathEditor,
  SvgToReact,
  SvgToDataUri,
  SlugGenerator,
  EmojiPickerTool,

  // Curl & Network
  CurlBuilder,
  CurlToFetch,
  CurlToAxios,
  TomlJsonConverter,
  CidrCalculator,
  SubnetCalculator,
  IpRangeCalculator,
  Ipv6Converter,
  MacAddressGenerator,
  AdSenseAuditor,
};

function Layout({ children }: { children: React.ReactNode }) {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [headerSearch, setHeaderSearch] = useState("");
  const searchDropdownRef = useRef<HTMLDivElement>(null);

  // Expand category automatically if it contains the active tool
  React.useEffect(() => {
    Object.entries(toolsByCategory).forEach(([category, tools]) => {
      if (tools.some(t => location.pathname === t.path)) {
        setExpandedCategories(prev => {
          const next = new Set(prev);
          next.add(category);
          return next;
        });
      }
    });
  }, [location.pathname]);

  // Click outside listener to close search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node)) {
        setHeaderSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const matchedTools = useMemo(() => {
    if (!headerSearch.trim()) return [];
    const query = headerSearch.toLowerCase();
    return toolRegistry.filter(tool => 
      tool.name.toLowerCase().includes(query) || 
      tool.description.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query)
    ).slice(0, 8);
  }, [headerSearch]);

  const handleRandomTool = () => {
    const randomIdx = Math.floor(Math.random() * toolRegistry.length);
    const randomTool = toolRegistry[randomIdx];
    navigate(randomTool.path);
    setHeaderSearch("");
  };

  const categoryIcons: Record<string, string> = {
    "Text & Word": "🔤",
    "Encoding / Decoding": "🔗",
    "JSON / Data": "{}",
    "JWT & Security": "🔑",
    "Security & Crypto": "🛡️",
    "Code Formatting": "💻",
    "Regex": "🔍",
    "Color": "🎨",
    "Date & Time": "🕒",
    "Web Dev & Network": "🌐",
    "Number & Unit": "🔢",
    "Misc": "🧩",
    "SVG Tools": "🎨",
    "Network Tools": "📶",
  };

  return (
    <div className="flex flex-col h-screen w-full bg-slate-50 font-sans text-slate-800 overflow-hidden">
      <header className="sticky top-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 shrink-0 z-50">
        <div className="flex items-center gap-4 sm:gap-8">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSidebarOpen(!isSidebarOpen)}
              className="p-2 -ml-2 rounded-md hover:bg-slate-100 lg:hidden"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xs italic tracking-tighter shrink-0">24/7</div>
              <span className="font-bold text-2xl tracking-tighter text-slate-900">247DevTools<span className="text-blue-600">Hub</span></span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <Link 
              to="/" 
              className={`h-16 flex items-center transition-colors ${location.pathname === "/" ? "text-slate-900 font-semibold border-b-2 border-blue-600" : "hover:text-slate-900"}`}
            >
              Tools
            </Link>
            <Link 
              to="/about" 
              className={`h-16 flex items-center transition-colors ${location.pathname === "/about" ? "text-slate-900 font-semibold border-b-2 border-blue-600" : "hover:text-slate-900"}`}
            >
              About
            </Link>
            <Link 
              to="/documentation" 
              className={`h-16 flex items-center transition-colors ${location.pathname === "/documentation" ? "text-slate-900 font-semibold border-b-2 border-blue-600" : "hover:text-slate-900"}`}
            >
              Documentation
            </Link>
            <Link 
              to="/adsense-auditor" 
              className={`h-16 flex items-center transition-colors ${location.pathname === "/adsense-auditor" ? "text-slate-900 font-semibold border-b-2 border-blue-600" : "hover:text-slate-900"}`}
            >
              AdSense Audit
            </Link>
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <div ref={searchDropdownRef} className="relative hidden sm:block">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="w-4 h-4" />
            </span>
            <input 
              type="text" 
              placeholder="Search tools..." 
              value={headerSearch}
              onChange={(e) => setHeaderSearch(e.target.value)}
              className="bg-slate-100 border border-transparent focus:border-blue-500 focus:bg-white rounded-full pl-10 pr-4 py-2 text-sm w-72 outline-none transition-all" 
            />
            {headerSearch && (
              <button 
                onClick={() => setHeaderSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Floating Dropdown Autocomplete Panel */}
            {headerSearch.trim() !== "" && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg max-h-96 overflow-y-auto z-50 divide-y divide-slate-50">
                <div className="px-3 py-2 bg-slate-50/50 text-xs font-semibold text-slate-400 tracking-wider uppercase">
                  Suggested Tools ({matchedTools.length})
                </div>
                {matchedTools.map(tool => (
                  <Link 
                    key={tool.id} 
                    to={tool.path}
                    onClick={() => setHeaderSearch("")}
                    className="block px-4 py-2.5 hover:bg-slate-50 text-left transition-colors group"
                  >
                    <div className="text-sm font-semibold text-slate-800 group-hover:text-blue-600">{tool.name}</div>
                    <div className="text-xs text-slate-400 truncate mt-0.5">{tool.description}</div>
                  </Link>
                ))}
                {matchedTools.length === 0 && (
                  <div className="px-4 py-4 text-center text-sm text-slate-400">
                    No matching tools found
                  </div>
                )}
              </div>
            )}
          </div>
          
          <button 
            onClick={handleRandomTool}
            title="I'm Feeling Lucky! (Random Tool)"
            className="w-9 h-9 rounded-full bg-slate-100 border flex items-center justify-center hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-all cursor-pointer"
          >
            <Wrench className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-slate-900/20 z-10 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-20 w-64 border-r border-slate-200 bg-white shrink-0 overflow-y-auto flex flex-col transform transition-transform duration-200 ease-in-out lg:static lg:translate-x-0 lg:overflow-y-hidden
            ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <div className="p-4 flex-1 lg:overflow-y-auto">
            <div className="lg:hidden flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
               <span className="font-bold text-slate-800">Menu</span>
               <button 
                 onClick={() => setSidebarOpen(false)} 
                 className="p-1 rounded hover:bg-slate-100"
                 aria-label="Close sidebar"
               >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
               </button>
            </div>
            <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-4">Tool Categories</h2>
            <nav className="space-y-1">
              {(Object.entries(toolsByCategory) as [ToolCategory, ToolDef[]][]).map(([category, tools]) => {
                const isActiveCategory = tools.some(t => location.pathname === t.path);
                const isExpanded = expandedCategories.has(category);
                return (
                 <div key={category} className="mb-1">
                    <button 
                      onClick={() => toggleCategory(category)}
                      aria-expanded={isExpanded}
                      aria-label={`Toggle ${category} category`}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${isActiveCategory ? "bg-slate-50 font-semibold text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-4 h-4 opacity-70 flex items-center justify-center">{categoryIcons[category] || "🔧"}</span> 
                        {category}
                      </div>
                      <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
                    </button>
                    {isExpanded && (
                      <div className="space-y-1 pl-10 mt-1 mb-2">
                        {tools.map(tool => (
                          <Link
                            key={tool.id}
                            to={tool.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`block px-3 py-1.5 text-sm rounded-md transition-colors ${
                              location.pathname === tool.path 
                                ? "bg-blue-50 text-blue-700 font-medium" 
                                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                            }`}
                          >
                            {tool.name}
                          </Link>
                        ))}
                      </div>
                    )}
                 </div>
                );
              })}
            </nav>
          </div>
          <div className="mt-auto p-4 border-t border-slate-100 bg-slate-50">
            <div className="p-3 bg-blue-700 rounded-lg text-white text-xs">
              <p className="font-bold mb-1 italic">Privacy Shield Enabled</p>
              <p className="text-blue-50 leading-relaxed">All processing happens locally in your browser. No data is ever sent to our servers.</p>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col min-w-0 bg-white overflow-y-auto">
          <div className="flex-1 w-full flex flex-col">
            {children}
          </div>
          {location.pathname !== "/" && <EcosystemSection variant="compact" />}
        </main>
      </div>

      <footer className="py-2 min-h-8 bg-slate-900 border-t border-slate-800 flex flex-wrap items-center justify-between gap-y-2 px-6 shrink-0">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-300">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Local-Only Mode
          </span>
          <span>v2.4.0</span>
          <span>Released: Oct 2023</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-300">
          <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
          <Link to="/documentation" className="hover:text-white transition-colors">Help & Docs</Link>
          <Link to="/adsense-auditor" className="hover:text-white transition-colors text-blue-400">AdSense Auditor</Link>
        </div>
      </footer>
    </div>
  );
}

function HomePage() {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTools = useMemo(() => {
    if (!searchQuery) return toolsByCategory;
    
    const query = searchQuery.toLowerCase();
    const result = {} as Record<string, ToolDef[]>;
    
    toolRegistry.forEach(tool => {
      if (tool.name.toLowerCase().includes(query) || tool.description.toLowerCase().includes(query)) {
        if (!result[tool.category]) result[tool.category] = [];
        result[tool.category].push(tool);
      }
    });
    
    return result;
  }, [searchQuery]);

  return (
    <div className="p-8">
      <SEO 
        title="Free Developer Tools Online" 
        description="A collection of fast, private, browser-based tools for developers. Format JSON, convert text, generate hashes, and more. No sign-ups required."
      />
      <div className="max-w-2xl mx-auto text-center mb-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Free Browser-Based Tools for Developers</h1>
        <p className="text-slate-600 text-lg mb-8">
          Fast, single-purpose utilities. No sign-ups, no tracking. All data is processed locally in your browser for total privacy.
        </p>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input 
            type="text" 
            placeholder="Search tools... (e.g., json formatter, base64)" 
            className="pl-12 h-14 text-lg rounded-full border-slate-300 shadow-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-12">
        {(Object.entries(filteredTools) as [string, ToolDef[]][]).map(([category, tools]) => tools && tools.length > 0 && (
          <div key={category}>
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              {category}
              <span className="text-sm font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{tools.length}</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tools.map(tool => (
                <Link 
                  key={tool.id} 
                  to={tool.path}
                  className="group flex flex-col p-5 bg-white border border-slate-200 rounded-xl hover:border-blue-400 hover:shadow-md transition-all duration-200"
                >
                  <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-blue-600 flex items-center justify-between">
                    {tool.name}
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed flex-1">
                    {tool.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        ))}
        {Object.keys(filteredTools).length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No tools found matching "{searchQuery}"
          </div>
        )}
      </div>

      <div className="mt-16 -mx-8 -mb-8">
        <EcosystemSection variant="full" />
      </div>
    </div>
  );
}

function ToolPageWrapper({ tool }: { tool: ToolDef }) {
  const Component = componentMap[tool.componentName] || (() => <PlaceholderTool name={tool.name} />);
  
  // Get related tools from same category
  const relatedTools = toolRegistry
    .filter(t => t.category === tool.category && t.id !== tool.id)
    .slice(0, 4);

  return (
    <div className="w-full">
      <SEO 
        title={tool.name} 
        description={tool.description}
      />
      <div className="p-6">
        <Component />
      </div>

      {relatedTools.length > 0 && (
        <section className="p-6 bg-slate-50 border-t border-slate-100 mt-6">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Related Tools</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {relatedTools.map(rt => (
              <Link 
                key={rt.id} 
                to={rt.path}
                className="p-3 bg-white border border-slate-200 rounded-md hover:border-blue-400 group flex flex-col"
              >
                <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-600">{rt.name}</p>
                <p className="text-xs text-slate-500 truncate">{rt.description}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <FileDropProvider>
          <Layout>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/api-info" element={<ApiInfo />} />

            {/* Category Pages */}
            <Route path="/text-word" element={<CategoryPage />} />
            <Route path="/encoding-tools" element={<CategoryPage />} />
            <Route path="/encoding-decoding" element={<CategoryPage />} />
            <Route path="/json-tools" element={<CategoryPage />} />
            <Route path="/csv-editor" element={<CategoryPage />} />
            <Route path="/data-tools" element={<CategoryPage />} />
            <Route path="/excel-editor" element={<CategoryPage />} />
            <Route path="/jwt-security" element={<CategoryPage />} />
            <Route path="/security-crypto" element={<CategoryPage />} />
            <Route path="/code-formatting" element={<CategoryPage />} />
            <Route path="/regex-tester" element={<CategoryPage />} />
            <Route path="/color-tools" element={<CategoryPage />} />
            <Route path="/color-picker" element={<CategoryPage />} />
            <Route path="/date-time" element={<CategoryPage />} />
            <Route path="/datetime-tools" element={<CategoryPage />} />
            <Route path="/dev-tools" element={<CategoryPage />} />
            <Route path="/web-dev-network" element={<CategoryPage />} />
            <Route path="/number-tools" element={<CategoryPage />} />
            <Route path="/svg-tools" element={<CategoryPage />} />
            <Route path="/misc-tools" element={<CategoryPage />} />
            <Route path="/network-tools" element={<CategoryPage />} />

            {toolRegistry.map(tool => (
              <React.Fragment key={tool.id}>
                <Route path={tool.path} element={<ToolPageWrapper tool={tool} />} />
              </React.Fragment>
            ))}
            <Route path="*" element={
              <div className="p-12 text-center">
                <SEO title="Page Not Found" description="The requested developer tool was not found on 247 DevTools Hub." />
                <h2 className="text-2xl font-bold mb-4">Tool not found</h2>
                <Link to="/" className="text-blue-600 hover:underline">Return to homepage</Link>
              </div>
            } />
          </Routes>
        </Layout>
        </FileDropProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
